import * as Yup from 'yup';
import { TIPOS_SINIESTRO_PERMITIDOS } from './config-ingreso-manual';

// ── Patente ─────────────────────────────────────────────────────────────────────────────────────
// Misma regla que el resto del sistema: formato antiguo (2 letras + 4 dígitos) o nuevo (4 consonantes del
// conjunto restringido + 2 dígitos), separador opcional (. - espacio) y largo 2–7.
// La clase de consonantes incluye la S (b c d f g h j k l p r s t v w x y z): en ogrdappcl-web quedó sin la S
// al consolidar la regla y rechazaba patentes válidas (SHHV25…). Mensaje: "La Patente no es válida".
export const RE_PATENTE = /^(?:[a-z]{2}[.\- ]?[0-9]{2}[.\- ]?[0-9]{2}|[b-df-hj-lprstv-z]{2}[.\- ]?[b-df-hj-lprstv-z]{2}[.\- ]?[0-9]{2})$/i;

export const patenteValida = (valor) => {
    const s = String(valor === null || valor === undefined ? '' : valor).trim();
    if (s === '') return true;
    return s.length >= 2 && s.length <= 7 && RE_PATENTE.test(s);
};

// La patente se guarda en mayúsculas y sin separadores (así están las existentes).
export const normalizaPatente = (valor) => String(valor || '').replace(/[.\- ]/g, '').trim().toUpperCase();

// ── Largos máximos (según las columnas de la BD; el lambda aplica los mismos) ───────────────────
// Una columna más corta que el texto escrito hace fallar el INSERT («Data too long») o lo recorta en silencio.
export const LIMITES = {
    lugarSiniestro: 500,  // administra_caso.adc_var_lugarSiniestro varchar(500)
    relato: 5000,         // administra_caso.adc_var_relatoDenuncio varchar(5000)
    aseNombre: 100,       // implicado_persona.aip_var_nombreRazonInvol varchar(100)
    conNombre: 100,
    terNombre: 100,
    aseDireccion: 100,    // implicado_persona.aip_var_direccionInvol varchar(100)
    siniestroIM: 20,      // adm_caso_implicado.aci_var_siniestroIM varchar(20)
    terDescripcion: 1000, // adm_caso_implicado.aci_var_detalledescripcion varchar(1000)
    comisaria: 70,        // antecedente_policial.aap_var_Comisaria varchar(70)
    poliza: 10,           // administra_caso.adc_var_poliza varchar(10)
    notasPreAnalisis: 255 // adm_caso_implicado.aci_var_notasPreAnalisis varchar(255)
};
// administra_caso.adc_sma_item es smallint.
export const MAX_ITEM_POLIZA = 32767;
// N° de siniestro: administra_caso/adm_ges_caso/... lo guardan en varchar(10), DESPUÉS de quitar puntos y lo que sigue al guion.
export const MAX_SINIESTRO = 10;
export const normalizaSiniestro = (valor) => String(valor || '').split('-')[0].replace(/\./g, '').trim();
// antecedente_policial.aap_var_nroPartePolicial es INT: el folio del parte debe ser un número entero.
export const MAX_FOLIO_POLICIAL = 2147483647;
// Montos: decimal(12,2) en liquida_siniestro_implicado y adm_caso_implicado ⇒ 10 dígitos enteros.
export const MAX_DIGITOS_MONTO = 10;
// implicado_persona guarda el teléfono como número: más de 15 dígitos pierden precisión.
export const MAX_DIGITOS_TELEFONO = 15;
// administra_caso.adc_tin_annoVh es smallint; se exige un año de 4 dígitos razonable.
export const ANIO_MINIMO = 1900;

// ── Rut ─────────────────────────────────────────────────────────────────────────────────────────
const calculaDV = (numero) => {
    let m = 0;
    let s = 1;
    for (let t = numero; t; t = Math.floor(t / 10)) {
        s = (s + (t % 10) * (9 - (m++ % 6))) % 11;
    }
    return s ? String(s - 1) : 'K';
};

// ── Formato de pantalla (rut y montos) ──────────────────────────────────────────────────────────
// En el formulario se ve con formato (12.345.678-9, 1.500.000), pero al servidor siempre va SIN formato.
export const conMiles = (digitos) => String(digitos || '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

// Solo dígitos y K; el último carácter es el dígito verificador. '123456789' → '12.345.678-9'.
export const formateaRut = (texto) => {
    const limpio = String(texto || '').replace(/[^0-9kK]/g, '').toUpperCase().slice(0, 10);
    if (limpio.length <= 1) return limpio;
    return `${conMiles(limpio.slice(0, -1))}-${limpio.slice(-1)}`;
};

// '12.345.678-9' → '12345678-9' (sin puntos, con guion, como lo envían las demás pantallas).
export const rutSinFormato = (texto) => {
    const limpio = String(texto || '').replace(/[^0-9kK]/g, '').toUpperCase();
    return limpio.length <= 1 ? limpio : `${limpio.slice(0, -1)}-${limpio.slice(-1)}`;
};

export const formateaMonto = (texto) => conMiles(String(texto || '').replace(/\D/g, ''));
export const montoSinFormato = (texto) => String(texto || '').replace(/\D/g, '');

// Vacío pasa (el rut es opcional en asegurado, conductor y tercero).
export const rutValido = (valor) => {
    const s = String(valor === null || valor === undefined ? '' : valor).trim();
    if (s === '') return true;
    const limpio = s.replace(/\./g, '').replace(/\s+/g, '').toUpperCase();
    const guion = limpio.indexOf('-');
    const numeroTxt = guion >= 0 ? limpio.slice(0, guion) : limpio.slice(0, -1);
    const dvTxt = guion >= 0 ? limpio.slice(guion + 1) : limpio.slice(-1);
    if (!/^\d{1,9}$/.test(numeroTxt) || !/^[\dK]$/.test(dvTxt)) return false;
    return calculaDV(Number(numeroTxt)) === dvTxt;
};

// ── Fechas ──────────────────────────────────────────────────────────────────────────────────────
const dos = (n) => String(n).padStart(2, '0');

// Hoy en formato AAAA-MM-DD (el que entrega <input type="date">), con la fecha local del navegador.
export const hoyISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
};

const horaValida = (valor) => {
    const s = String(valor || '').trim();
    if (s === '') return true;
    const match = s.match(/^(\d{1,2}):?(\d{2})$/);
    if (!match) return false;
    return Number(match[1]) <= 23 && Number(match[2]) <= 59;
};

// ── Esquema de validación (Yup) ─────────────────────────────────────────────────────────────────
const OBLIGATORIO = 'Este campo es obligatorio';
const noFutura = (valor) => !valor || valor <= hoyISO();

const rut = Yup.string().test('rut', 'El rut no es válido', rutValido);
const patenteOpcional = Yup.string().test('patente', 'La Patente no es válida', patenteValida);
const textoMax = (campo) => Yup.string().max(LIMITES[campo], `No puede superar ${LIMITES[campo]} caracteres`);
const digitos = (valor) => String(valor || '').replace(/\D/g, '');
const telefono = Yup.string().test('telefono', `No puede tener más de ${MAX_DIGITOS_TELEFONO} dígitos`,
    (v) => digitos(v).length <= MAX_DIGITOS_TELEFONO);
const montoMax = Yup.string().test('monto-max', `No puede tener más de ${MAX_DIGITOS_MONTO} dígitos`,
    (v) => digitos(v).length <= MAX_DIGITOS_MONTO);

export const ingresoManualSchema = Yup.object({
    siniestro: Yup.string().trim().required(OBLIGATORIO)
        .test('largo', `El N° de siniestro no puede superar ${MAX_SINIESTRO} caracteres (sin puntos)`,
            (v) => normalizaSiniestro(v).length <= MAX_SINIESTRO),
    patente: Yup.string().trim().required(OBLIGATORIO).test('patente', 'La Patente no es válida', patenteValida),
    fechaSiniestro: Yup.string().required(OBLIGATORIO)
        .test('no-futura', 'No puede ser una fecha futura', noFutura),
    fechaDenuncio: Yup.string().required(OBLIGATORIO)
        .test('no-futura', 'No puede ser una fecha futura', noFutura)
        .test('posterior', 'No puede ser anterior a la fecha del siniestro', function (valor) {
            const { fechaSiniestro } = this.parent;
            return !valor || !fechaSiniestro || valor >= fechaSiniestro;
        }),
    hora: Yup.string().test('hora', 'La hora debe tener formato HH:MM', horaValida),
    tipoSiniestro: Yup.string().required(OBLIGATORIO)
        .oneOf(TIPOS_SINIESTRO_PERMITIDOS, 'El tipo de siniestro debe ser Pérdida Total o Pérdida Parcial'),
    anioVehiculo: Yup.string()
        .test('anio', 'El año debe tener 4 dígitos', (v) => !v || /^\d{4}$/.test(v))
        .test('anio-rango', `El año debe estar entre ${ANIO_MINIMO} y ${new Date().getFullYear() + 1}`,
            (v) => !v || !/^\d{4}$/.test(v) || (Number(v) >= ANIO_MINIMO && Number(v) <= new Date().getFullYear() + 1)),
    lugarSiniestro: textoMax('lugarSiniestro'),
    relato: textoMax('relato'),
    // Campos opcionales de RG-591. Causal, color y uso son códigos de combo (el servidor revisa que estén vigentes).
    causal: Yup.string(),
    colorVehiculo: Yup.string(),
    usoVehiculo: Yup.string(),
    poliza: textoMax('poliza'),
    notasPreAnalisis: textoMax('notasPreAnalisis'), // solo se pide en modo Defensa; el límite aplica igual
    itemPoliza: Yup.string().test('item', `El ítem debe ser un número entre 1 y ${MAX_ITEM_POLIZA}`,
        (v) => !v || (/^\d{1,5}$/.test(v) && Number(v) >= 1 && Number(v) <= MAX_ITEM_POLIZA)),
    vigenciaDesde: Yup.string().test('vigencia-par', 'Informe también el fin de la vigencia', function (v) {
        return !v || !!this.parent.vigenciaHasta;
    }),
    vigenciaHasta: Yup.string()
        .test('vigencia-par', 'Informe también el inicio de la vigencia', function (v) {
            return !v || !!this.parent.vigenciaDesde;
        })
        .test('vigencia-orden', 'El fin de la vigencia no puede ser anterior al inicio', function (v) {
            const { vigenciaDesde } = this.parent;
            return !v || !vigenciaDesde || v >= vigenciaDesde;
        }),
    aseRut: rut,
    aseNombre: textoMax('aseNombre'),
    aseDireccion: textoMax('aseDireccion'),
    aseFono: telefono,
    conRut: rut,
    conNombre: textoMax('conNombre'),
    conFono: telefono,
    terRut: rut,
    terNombre: textoMax('terNombre'),
    terPatente: patenteOpcional,
    // Compañía y N° de siniestro de la CIA son opcionales: el abogado externo los completa al derivar a Inter Cía.
    idCiaImplicado: Yup.string(),
    siniestroIM: textoMax('siniestroIM'),
    fechaSiniestroIM: Yup.string().test('no-futura', 'No puede ser una fecha futura', noFutura),
    terDescripcion: textoMax('terDescripcion'),
    comisaria: textoMax('comisaria'),
    folioPolicial: Yup.string().test('folio', 'El folio del parte debe ser un número entero (máximo 2.147.483.647)',
        (v) => !v || (/^\d{1,10}$/.test(v) && Number(v) <= MAX_FOLIO_POLICIAL)),
    // Los montos son opcionales; si se informan, enteros en pesos.
    costo: montoMax.test('costo', 'Debe ser un monto entero en pesos, sin puntos', (v) => !v || /^[1-9]\d*$/.test(v)),
    provision: montoMax.test('provision', 'Debe ser un monto entero en pesos, sin puntos', (v) => !v || /^\d+$/.test(v)),
    idResponsable: Yup.string().required(OBLIGATORIO)
});

// Carta de cobro adjunta (RG-591, fase 2b): documento 203. El archivo viaja en base64 por dos lambdas (tope de 6 MB cada una).
export const MAX_BYTES_CARTA = 4 * 1024 * 1024;
export const EXTENSIONES_CARTA = ['DOC', 'DOCX', 'PDF'];
export const extensionDeArchivo = (nombre) => {
    const partes = String(nombre || '').split('.');
    return partes.length > 1 ? partes.pop().trim().toUpperCase() : '';
};

// Contenido del archivo en base64, SIN el prefijo data-URL (como lo espera la carga de documentos).
export const leeArchivoBase64 = (archivo) => new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(String(lector.result).split(',')[1] || '');
    lector.onerror = () => reject(new Error('No se pudo leer el archivo de la carta.'));
    lector.readAsDataURL(archivo);
});

// ── Modo DEFENSA (cartas inter cía, RG-591): mismos campos y reglas, más estos obligatorios; el responsable no se elige. ──────────
// «Valor neto solicitado» es el campo costo; «Monto a pagar» es montoPagar (no puede superar al valor neto).
const esquemaDefensa = ingresoManualSchema.shape({
    idResponsable: Yup.string(),
    idCiaImplicado: Yup.string().required(OBLIGATORIO),
    siniestroIM: textoMax('siniestroIM').trim().required(OBLIGATORIO),
    terPatente: Yup.string().trim().required(OBLIGATORIO).test('patente', 'La Patente no es válida', patenteValida),
    costo: montoMax.required(OBLIGATORIO).test('costo', 'Debe ser un monto entero en pesos, sin puntos', (v) => !v || /^[1-9]\d*$/.test(v)),
    montoPagar: montoMax.required(OBLIGATORIO)
        .test('monto-pagar', 'Debe ser un monto entero en pesos, sin puntos', (v) => !v || /^[1-9]\d*$/.test(v))
        .test('monto-pagar-tope', 'No puede ser mayor que el valor neto solicitado', function (v) {
            const { costo } = this.parent;
            return !v || !/^\d+$/.test(costo || '') || Number(v) <= Number(costo);
        }),
    fechaRecepcionCarta: Yup.string().required(OBLIGATORIO).test('no-futura', 'No puede ser una fecha futura', noFutura),
    notasPreAnalisis: Yup.string().max(LIMITES.notasPreAnalisis, `No puede superar ${LIMITES.notasPreAnalisis} caracteres`),
    // Carta de cobro (opcional): documento «Carta Cobro CIA»; el servidor valida lo mismo contra cdi_tipoDocumento.
    cartaArchivo: Yup.mixed().nullable()
        .test('carta-formato', `Formato no válido. Se acepta: ${EXTENSIONES_CARTA.join(', ')}.`,
            (f) => !f || EXTENSIONES_CARTA.includes(extensionDeArchivo(f.name)))
        .test('carta-tamano', 'La carta no puede superar los 4 MB.', (f) => !f || f.size <= MAX_BYTES_CARTA)
});

// El esquema depende del modo (flujo) del usuario.
export const esquemaPara = (modo) => (modo === 'DEFENSA' ? esquemaDefensa : ingresoManualSchema);

// % de convenio que calculará el servidor: round(monto a pagar × 100 / valor neto). '' si faltan datos.
export const porcentajeConvenio = (costo, montoPagar) => {
    const c = Number(montoSinFormato(costo));
    const m = Number(montoSinFormato(montoPagar));
    return c > 0 && m > 0 ? Math.round((m * 100) / c) : '';
};

// ── Valores del formulario y payload ────────────────────────────────────────────────────────────
// Los nombres de los campos del formulario son los mismos que recibe el lambda recupero-fnc-ingreso-manual-intercia.
export const valoresIniciales = (idResponsable = '') => ({
    siniestro: '', patente: '', fechaSiniestro: '', fechaDenuncio: '', hora: '', tipoSiniestro: '',
    comunaSiniestro: '', ciudadSiniestro: '', lugarSiniestro: '', relato: '',
    // Opcionales de RG-591: causal (concepto 300) y datos de la póliza.
    causal: '', poliza: '', itemPoliza: '', vigenciaDesde: '', vigenciaHasta: '',
    // codModelo es el código del modelo elegido en el combo (depende de la marca); modelo guarda su nombre (para el resumen).
    tipoVehiculo: '', marcaVehiculo: '', codModelo: '', modelo: '', anioVehiculo: '', colorVehiculo: '', usoVehiculo: '',
    aseRut: '', aseNombre: '', aseFono: '', aseDireccion: '', aseComuna: '', aseCiudad: '',
    terRut: '', terNombre: '', terPatente: '', terMarca: '',
    idCiaImplicado: '', siniestroIM: '', fechaSiniestroIM: '', terDescripcion: '',
    conRut: '', conNombre: '', conFono: '',
    costo: '', provision: '',
    // Solo modo Defensa (cartas inter cía): monto a pagar, fecha de recepción de la carta y notas de pre-análisis.
    montoPagar: '', fechaRecepcionCarta: '', notasPreAnalisis: '',
    cartaArchivo: null, // archivo (File) de la carta de cobro; opcional
    comisaria: '', folioPolicial: '', fechaConstancia: '',
    idResponsable
});

export const nombreDe = (lista, id) => {
    const encontrado = (lista || []).find((item) => String(item.id) === String(id));
    return encontrado ? encontrado.nombre : '';
};

const CAMPOS_RUT = ['aseRut', 'terRut', 'conRut'];
const CAMPOS_MONTO = ['costo', 'provision', 'montoPagar'];

// Valores para precargar el formulario con un caso existente (modo edición): se parte de los iniciales para que ningún campo
// quede sin definir y se descartan los que el servidor no manda como texto.
export const valoresDeCaso = (valoresServidor) => {
    const resultado = valoresIniciales();
    Object.keys(resultado).forEach((campo) => {
        const v = valoresServidor ? valoresServidor[campo] : undefined;
        if (v !== undefined && v !== null) resultado[campo] = String(v);
    });
    return resultado;
};

// Solo se envía lo informado; el lambda completa el resto con los valores del sistema.
// extra: { modo, idCaso } — modo = flujo de ingreso (el servidor lo vuelve a validar contra los roles); idCaso = caso que se edita.
export const construyePayload = (valores, { rutUsuario, rutOrganizacion }, marcas, extra = {}) => {
    const payload = {};
    Object.keys(valores).forEach((campo) => {
        const valor = typeof valores[campo] === 'string' ? valores[campo].trim() : valores[campo];
        if (valor !== '' && valor !== null && valor !== undefined) payload[campo] = valor;
    });
    // Rut y montos se muestran con formato; al servidor van sin formato.
    CAMPOS_RUT.forEach((campo) => { if (payload[campo]) payload[campo] = rutSinFormato(payload[campo]); });
    CAMPOS_MONTO.forEach((campo) => { if (payload[campo]) payload[campo] = montoSinFormato(payload[campo]); });
    // La carta (File) no viaja en el JSON: se informan sus datos y el contenido en base64 se agrega solo al confirmar (ver index.js).
    delete payload.cartaArchivo;
    const carta = valores.cartaArchivo;
    if (carta && extra.modo === 'DEFENSA') {
        payload.filename = carta.name;
        payload.mimetype = carta.type || 'application/octet-stream';
        payload.fileSizeBytes = carta.size;
    }
    payload.idOrganizacion = String(rutOrganizacion);
    payload.idUsuario = String(rutUsuario);
    if (payload.marcaVehiculo) payload.marcaTexto = nombreDe(marcas, payload.marcaVehiculo);
    if (extra.modo) payload.modo = extra.modo;
    if (extra.idCaso) payload.idCaso = extra.idCaso;
    return payload;
};

// La ciudad se determina por la comuna: el endpoint "generales" entrega, en cada comuna, el id de su ciudad (atr5) y
// el cod_unico de la ciudad (atr2). Devuelve '' si la comuna no existe o no tiene ciudad.
export const ciudadDeComuna = (comunas, ciudades, idComuna) => {
    const comuna = (comunas || []).find((c) => String(c.id).trim() === String(idComuna).trim());
    if (!comuna) return '';
    if (comuna.atr5 && String(comuna.atr5).trim() !== '') return String(comuna.atr5).trim();
    const ciudad = (ciudades || []).find((c) => c.codUnico && comuna.atr2 && c.codUnico === comuna.atr2);
    return ciudad ? String(ciudad.id).trim() : '';
};

// Nombre de la ciudad de la comuna, para mostrarlo (la comuna trae el nombre en atr4). No se usa el id para buscarlo en el
// catálogo de ciudades porque ese catálogo repite ids (MAG = MAGALLANES y MARGA MARGA): se busca por cod_unico.
export const nombreCiudadDeComuna = (comunas, ciudades, idComuna) => {
    const comuna = (comunas || []).find((c) => String(c.id).trim() === String(idComuna).trim());
    if (!comuna) return '';
    if (comuna.atr4 && String(comuna.atr4).trim() !== '') return String(comuna.atr4).trim();
    const ciudad = (ciudades || []).find((c) => c.codUnico && comuna.atr2 && c.codUnico === comuna.atr2);
    return ciudad ? String(ciudad.nombre).trim() : '';
};

// Los errores del servidor vienen como [{ campo, mensaje }]; el formulario los espera como { campo: mensaje }.
export const erroresAMapa = (errores) => (errores || []).reduce((mapa, e) => ({ ...mapa, [e.campo]: e.mensaje }), {});
